import { gql } from '@apollo/client';

export const commonAssetFields = `
    id
    name
    serialNumber
    tagNumber
    alternateTagNumber
    description
    thirdPartyId
    displayImageId
    updated
    created
    lastScanned
    verificationStatus {
        id
        name
    }
    requiresTagNumber
    assetStatus {
        id
        name
    }
    parentFolder {
        id
        name
        path
    }
    parentAsset {
        id
        name
    }
    model {
        id
        name
        defaultClass
        description
        height
        width
        depth
        numberOfSlots
        productLine
        useRackUnits
        assetType {
            id
            name
        }
        manufacturer {
            id
            name
        }
    }
    customer {
        id
        name
    }
    group {
        id
        name
    }
    userDefinedFields {
        number
        type
        name
        value
    }`;

export const ASSETS_SEARCH = gql`query AssetSearch(
    $first: Int!
    $after: String
    $orderBy: [OrderBy!]
    $where: [WhereExpression!]
) {
    assets(
        first: $first
        after: $after
        orderBy: $orderBy
        where: $where
    ) {
        totalCount
        pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
        }
        edges {
            node {
                ${commonAssetFields}
                leafFolderPath
            }
        }
    }
}`;

export const GET_ASSET_DETAILS = gql`query GetAssetById($id: ID!) {
    asset(id: $id) {
        ${commonAssetFields}
        childAssets {
            id
            name
            verificationStatus {
                id
                name
            }
            childAssets {
                id
                name
                verificationStatus {
                    id
                    name
                }
            }
        }
        dataCenterProperties {
            bottomPositionOrSlot
            leftPosition
            rackPosition
            labelX
            labelY
            isNonDataCenter
            rotate
        }
    }
}`;

export const FIND_ASSETS = gql`
    query FindAllAssets(
        $first: Int!
        $after: String
        $orderBy: [OrderBy!]
        $where: [WhereExpression!]
    ) {
        assets(first: $first, after: $after, orderBy: $orderBy, where: $where) {
            totalCount
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
            edges {
                node {
                    id
                    name
                    serialNumber
                    tagNumber
                    model {
                        id
                        name
                        defaultClass
                    }
                    group {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const GET_ASSET_CHILDREN = gql`
    query GetChildrenOfBaseAsset($id: ID!) {
        asset(id: $id) {
            id
            name
            displayImageId
            verificationStatus {
                id
                name
            }
            group {
                id
                name
            }
            childAssets {
                id
                name
                displayImageId
                verificationStatus {
                    id
                    name
                }
                group {
                    id
                    name
                }
                childAssets {
                    id
                    name
                    displayImageId
                    verificationStatus {
                        id
                        name
                    }
                    group {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const HAS_CHILD_ASSETS = gql`
    query HasChildAssets($id: [String]) {
        hasChildAssets: anyAssetInAccount(where: [{ path: "parentAssetId", value: $id }])
    }
`;

export const getAssetValidationQuery = fields => {
    const validations = fields.reduce((q, x) => (x.value ? (q += validationSection(x)) : q), '');
    const param = validations ? '($id: [String])' : '';

    return gql`query ValidateAsset ${param} {
        myAccount {
            id
            name
            licenseCount
            licenseLimit
            whiteLabelName
            totalFolders
            totalAssets
            totalAssetsInAccount
        }
        ${validations}
    }`;
};

const validationSection = ({ alias, path, value }) => `${alias}: anyAssetInAccount(
    where: [
        { path: "${path}", value: ${format(value)} }
        { path: "id", value: $id, negate: true }
    ]
)`;

const format = value => JSON.stringify(value?.toString() ?? '');

export const GET_ASSET_ANCESTORS = gql`
    query GetAssetAncestors($id: ID!) {
        asset(id: $id) {
            id
            parentAsset {
                id
                name
                parentAsset {
                    id
                    name
                }
            }
            allFolders {
                id
                name
                path
            }
        }
    }
`;

export const FIND_ATTACHMENTS_BY_ASSET = gql`
    query FindAttachmentsByAsset(
        $first: Int!
        $after: String
        $orderBy: [OrderBy!]
        $where: [WhereExpression!]
    ) {
        assetAttachments(first: $first, after: $after, orderBy: $orderBy, where: $where) {
            totalCount
            pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
            }
            edges {
                node {
                    id
                    assetId
                    attachment {
                        id
                        filename
                        mimeType
                        fileSizeBytes
                        hasPreview
                        supportsPreview
                        created
                        createdBy
                    }
                }
            }
        }
    }
`;

export const FIND_ASSETS_AND_ANCESTORS = gql`
    query FindAssetsAndAncestors($first: Int!, $where: [WhereExpression!]) {
        assets(first: $first, where: $where) {
            totalCount
            edges {
                node {
                    id
                    parentAsset {
                        id
                        name
                        parentAsset {
                            id
                            name
                        }
                    }
                    allFolders {
                        id
                        name
                        path
                    }
                }
            }
        }
    }
`;

export const FIND_ASSETS_AND_ANCESTORS_BY_TAG_NUMBER = gql`
    query FindAssetsAndAncestorsByTagNumber($tagNumber: [String], $tagNumberSuffix: [String]) {
        primaryMatches: assets(first: 1, where: [{ path: "tagNumber", value: $tagNumber }]) {
            totalCount
            edges {
                node {
                    id
                    parentAsset {
                        id
                        name
                        parentAsset {
                            id
                            name
                        }
                    }
                    allFolders {
                        id
                        name
                        path
                    }
                }
            }
        }

        secondaryMatches: assets(
            first: 1
            where: [{ path: "tagNumber", value: $tagNumberSuffix, comparison: endsWith }]
        ) {
            totalCount
            edges {
                node {
                    id
                    parentAsset {
                        id
                        name
                        parentAsset {
                            id
                            name
                        }
                    }
                    allFolders {
                        id
                        name
                        path
                    }
                }
            }
        }
    }
`;
